import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";

import { type JourneyPhaseKind, JOURNEY_STATES } from "./types";
import { useSwitchJourneyPhase } from "./useSwitchJourneyPhase";

// Toggle + popover explaining the coach lifecycle states. Closes on outside
// click or Escape (Radix Popover). With `confirmClear`, switching stage first
// warns that the current chat will be cleared.
export function JourneyInfo({
  activeKind,
  confirmClear = false,
}: {
  activeKind?: JourneyPhaseKind;
  confirmClear?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<JourneyPhaseKind | null>(null);
  const switchPhase = useSwitchJourneyPhase();

  const switchTo = (kind: JourneyPhaseKind) => {
    setOpen(false);
    setPending(null);
    switchPhase(kind);
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPending(null);
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className="text-xs px-2 py-1 rounded-full bg-indigo-600/10 text-indigo-600 font-medium whitespace-nowrap"
        >
          ⓘ How your journey works
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={16}
          className="z-[70] flex flex-col gap-2 rounded-lg p-3 w-[min(24rem,calc(100vw-2rem))] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto bg-bg-primary border border-border shadow-lg"
        >
          {pending ? (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-md p-2">
                Switching to{" "}
                <span className="font-semibold">
                  {JOURNEY_STATES.find((s) => s.kind === pending)?.label}
                </span>{" "}
                will clear your current chat.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border text-text-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => switchTo(pending)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700"
                >
                  Switch and clear chat
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-indigo-600">
                Your coach moves through these stages based on your profile,
                targets and completions. Each stage changes what the coach asks
                and which tools it offers. Select a stage to talk to the coach
                in that mode.
              </p>
              <ol className="flex flex-col gap-1">
                {JOURNEY_STATES.map((s, i) => {
                  const isActive = s.kind === activeKind;
                  const content = (
                    <>
                      <span className="font-semibold">
                        {i + 1}. {s.label}
                        {isActive && " (current)"}
                      </span>
                      <span className="block opacity-80">{s.description}</span>
                    </>
                  );
                  const base = `text-xs text-left w-full rounded-md px-2 py-1 text-indigo-600 ${isActive ? "bg-indigo-600/20" : ""}`;
                  return (
                    <li key={s.kind}>
                      <button
                        type="button"
                        onClick={() => {
                          if (!confirmClear) switchTo(s.kind);
                          else if (s.kind === activeKind) setOpen(false);
                          else setPending(s.kind);
                        }}
                        className={`${base} hover:bg-indigo-600/20`}
                      >
                        {content}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
